import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { OfferRejectionReasonCode } from '../types/offers';
import type { AdminPackageDetail } from '../types/packages';
import DecisionBar from '../components/offers/DecisionBar';
import PackagePhonePreview from '../components/packages/PackagePhonePreview';
import PackageFactsPanel from '../components/packages/PackageFactsPanel';
import PackagePolicyChecklist from '../components/packages/PackagePolicyChecklist';
import PackageActivityCard from '../components/packages/PackageActivityCard';
import { packageDecisionError, toDecisionOffer } from '../components/packages/packageReviewModel';

const QUEUE_PATH = '/service-approvals?tab=packages';

type Decision = 'approve' | 'reject' | 'suspend';

/** Spec 081b Flow D — `/service-approvals/packages/:id`: preview, decision, facts, policy checklist, history. */
export default function PackageReviewPage() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  /** Set by the edit page after a save. */
  const savedToast = (location.state as { toast?: string } | null)?.toast ?? null;

  const [detail, setDetail] = useState<AdminPackageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!auth.token || !id) return;
    setLoading(true);
    setLoadError(null);
    try {
      setDetail(await api.getPackageForReview(id, auth.token));
    } catch (err) {
      setLoadError(packageDecisionError(err, 'load').message);
    } finally {
      setLoading(false);
    }
  }, [auth.token, id]);

  useEffect(() => { load(); }, [load]);

  const decide = async (action: Decision, call: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setActionError(null);
    try {
      await call();
      navigate(QUEUE_PATH, { state: { toast: done } });
    } catch (err) {
      const mapped = packageDecisionError(err, action);
      setActionError(mapped.message);
      if (mapped.reload) await load();
    } finally {
      setBusy(false);
    }
  };

  if (loading && !detail) {
    return <div className="admin-loading"><div className="admin-loading-spinner"></div></div>;
  }

  if (!detail) {
    return (
      <div>
        <Link to={QUEUE_PATH} className="admin-link">← Back to packages</Link>
        <div className="admin-info-banner danger" role="alert" style={{ marginTop: '1rem' }}>
          {loadError ?? 'Could not load this package.'}
        </div>
      </div>
    );
  }

  const token = auth.token!;
  const { version } = detail;
  const canEdit = detail.status !== 'ENDED';

  return (
    <div className="offer-review package-review">
      <div className="offer-review__header">
        <Link to={QUEUE_PATH} className="admin-link">← Back to packages</Link>
        <div className="offer-review__title-row">
          <h2 className="offer-review__title">{detail.nameEn}</h2>
          {canEdit && (
            <Link to={`/service-approvals/packages/${detail.id}/edit`} className="admin-btn admin-btn-outline admin-btn-sm"
              data-testid="package-edit-link">
              Edit package
            </Link>
          )}
        </div>
        <div className="admin-text-muted">{detail.garage.name}</div>
      </div>

      {savedToast && <div className="admin-info-banner success" role="status" style={{ marginBottom: '1rem' }}>{savedToast}</div>}
      {actionError && (
        <div className="admin-info-banner danger" role="alert" data-testid="package-action-error" style={{ marginBottom: '1rem' }}>
          {actionError}
        </div>
      )}
      {loadError && <div className="admin-info-banner danger" role="alert" style={{ marginBottom: '1rem' }}>{loadError}</div>}

      <div className="offer-review__grid">
        <div className="offer-review__preview">
          <PackagePhonePreview pkg={detail} garageName={detail.garage.name} />
        </div>
        <div className="offer-review__side">
          <DecisionBar
            key={detail.id}
            offer={toDecisionOffer(detail)}
            busy={busy || loading}
            onApprove={() => decide('approve', () => api.approvePackage(detail.id, { version }, token), 'Package approved')}
            onReject={(reasonCode: OfferRejectionReasonCode, reason: string) =>
              decide('reject', () => api.rejectPackage(detail.id, { reasonCode, reason, version }, token), 'Package rejected')}
            onSuspend={(reason: string) =>
              decide('suspend', () => api.suspendPackage(detail.id, { reason, version }, token), 'Package suspended')}
          />
          <PackageFactsPanel detail={detail} />
          <PackagePolicyChecklist detail={detail} />
          <PackageActivityCard packageId={detail.id} token={token} refreshKey={version} />
        </div>
      </div>
    </div>
  );
}
